import { FieldInputProps, FieldMetaProps } from 'formik';
import { memoize } from 'lodash-es';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDebounce } from 'react-use';

import * as constants from 'utils/constants';

import * as Interfaces from './interfaces';
import * as Styled from './styled';

export type ComponentProps = {
  /**
   * Field information
   */
  field: FieldInputProps<string>;
  /**
   * metadata information
   */
  meta: FieldMetaProps<string>;
  /**
   * Optional render mode for the date either in full (dd/mm/yyyy) or partial (mm/yyyy). Defaults to "FullDate"
   */
  mode?: Interfaces.Mode;
  /**
   * Optional divider char between each date section (defaults to '/')
   */
  divider?: string;
  /**
   * Optional overriding accessibility label for screen-reader users. Defaults to 'date'
   */
  ariaLabel?: string;
  /**
   * (Optional) Disabled the date input from been editable
   */
  disabled: boolean;
  /**
   * Optional character to cover unfilled parts of the mask. Defaults to unfilled parts will be empty string.
   */
  maskChar?: string;
  /**
   * Number of ms to wait before invoking a function callback
   */
  delay?: number;
};

const getConfig = memoize(
  (divider: string, mode: Interfaces.Mode) => {
    switch (mode) {
      case 'FullDate':
        return {
          placeholder: `DD ${divider} MM ${divider} YYYY`,
          mask: `99 ${divider} 99 ${divider} 9999`
        };
      case 'MonthYear':
        return {
          placeholder: `MM ${divider} YYYY`,
          mask: `99 ${divider} 9999`
        };
      case 'MonthYearShort':
        return {
          placeholder: `MM ${divider} YY`,
          mask: `99 ${divider} 99`
        };
    }
  },
  (divider, mode) => `${divider}|${mode}`
);

const Input: React.FC<ComponentProps> = ({
  maskChar = null,
  mode = 'FullDate',
  divider = '/',
  field,
  meta,
  ariaLabel = 'date',
  disabled,
  delay = constants.INPUT_DELAY
}) => {
  const [innerValue, setInnerValue] = useState('');
  const { value } = field;
  const changeEventRef = useRef<React.ChangeEvent<HTMLInputElement>>();

  const isError = meta.touched && meta.error ? true : false;

  /**
   * Using a debounce effect for the input to avoid having the field calling handleChange on every key stroke.
   * ref:
   * https://medium.com/@vlad_19457/reducing-the-number-of-renders-when-using-formik-9790bf111ab9)
   * https://github.com/streamich/react-use/blob/master/docs/useDebounce.md
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  useDebounce(
    () => {
      if (changeEventRef.current) {
        field.onChange(changeEventRef.current);
      }
    },
    delay,
    [changeEventRef.current]
  );

  // Effect to update the input field internal value
  useEffect(() => {
    setInnerValue(value ? value : '');
  }, [value]);

  /**
   * Event handler when input value gets changed
   */
  const handleOnChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    event.persist();
    setInnerValue(event.currentTarget.value);
    changeEventRef.current = event;
  }, []);

  const { placeholder, mask } = getConfig(divider, mode);

  /* eslint-disable @typescript-eslint/no-unused-vars */
  return (
    <Styled.StyledInputMask
      {...field}
      isError={isError}
      mode={mode}
      value={innerValue}
      onChange={handleOnChange}
      placeholder={placeholder}
      aria-label={ariaLabel}
      mask={mask}
      disabled={disabled}
      maskChar={maskChar}
      aria-invalid={!!isError}
      aria-labelledby={`${field.name}.label`}
      autoComplete='off'>
      {({ isError, ...rest }) => <input disabled={disabled} type='tel' {...rest} />}
    </Styled.StyledInputMask>
  );
};

export default React.memo(Input);
